/**
 * Regole email (Fase 3): lista regole di smistamento dell'utente + builder.
 * Le regole si applicano ai messaggi NUOVI in arrivo in INBOX durante il
 * sync. Da qui: crea/modifica/elimina, attiva/disattiva, prova senza
 * effetti (dry-run) e applicazione retroattiva ai messaggi esistenti
 * (backfill). Tutti i dropdown sono ricercabili (SearchSelect locale).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Pencil, Play, Plus, Trash2, X } from 'lucide-react';
import { Badge, Button, Card, Input, Label } from '@floraltech/ui';
import {
  emailFetch,
  listaEtichette,
  REGOLA_AZIONI,
  REGOLA_CAMPI,
  REGOLA_OPERATORI,
  type BackfillEsito,
  type DryRunEsito,
  type EmailAccount,
  type EmailCartella,
  type EmailEtichetta,
  type EmailRegola,
  type RegolaCondizione,
} from '../lib/api';
import { SearchSelect, type Option } from '../components/SearchSelect';

const OPZIONI_SI_NO: Option[] = [
  { value: 'true', label: 'Si' },
  { value: 'false', label: 'No' },
];

const TUTTE_LE_CASELLE = '';

interface CondizioneForm {
  campo: string;
  operatore: string;
  valore: string;
}

export function RegolePage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const filtroAccount = searchParams.get('account') ?? TUTTE_LE_CASELLE;

  const [regole, setRegole] = useState<EmailRegola[]>([]);
  const [accounts, setAccounts] = useState<EmailAccount[]>([]);
  const [cartelle, setCartelle] = useState<EmailCartella[]>([]);
  const [etichette, setEtichette] = useState<EmailEtichetta[]>([]);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState<string | null>(null);
  const [salvataggio, setSalvataggio] = useState(false);

  // Builder: null = chiuso, '' = nuova regola, altrimenti id in modifica.
  const [editId, setEditId] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [accountId, setAccountId] = useState<string>(TUTTE_LE_CASELLE);
  const [matchTipo, setMatchTipo] = useState<'all' | 'any'>('all');
  const [condizioni, setCondizioni] = useState<CondizioneForm[]>([
    { campo: 'mittente', operatore: 'contiene', valore: '' },
  ]);
  const [azioniSel, setAzioniSel] = useState<Set<string>>(new Set());
  const [cartellaSposta, setCartellaSposta] = useState('');
  const [etichettaApplicata, setEtichettaApplicata] = useState('');
  const [stopSuMatch, setStopSuMatch] = useState(false);
  const [priorita, setPriorita] = useState(0);

  const [dryRun, setDryRun] = useState<DryRunEsito | null>(null);
  const [backfill, setBackfill] = useState<BackfillEsito | null>(null);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      const [resRegole, resAccounts, etichetteAttive] = await Promise.all([
        emailFetch('/regole'),
        emailFetch('/account'),
        listaEtichette(),
      ]);
      if (resRegole.ok) setRegole(await resRegole.json());
      if (resAccounts.ok) setAccounts(await resAccounts.json());
      setEtichette(etichetteAttive);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  // Cartelle dell'account scelto (servono per l'azione sposta)
  useEffect(() => {
    if (!accountId) {
      setCartelle([]);
      return;
    }
    (async () => {
      const res = await emailFetch(`/cartelle?account_id=${accountId}`);
      setCartelle(res.ok ? await res.json() : []);
    })();
  }, [accountId]);

  // Le regole automatiche non si applicano alla PEC (Fase 4 T5, esclusione
  // esplicita lato backend): niente caselle PEC nei dropdown di filtro/form.
  const opzioniAccountFiltro: Option[] = [
    { value: TUTTE_LE_CASELLE, label: 'Tutte le caselle' },
    ...accounts.filter((a) => a.tipo !== 'pec').map((a) => ({ value: a.id, label: a.indirizzo })),
  ];
  const opzioniCartelle: Option[] = cartelle.map((c) => ({
    value: c.id,
    label: c.nome_display ?? c.nome_imap,
  }));
  const opzioniEtichette: Option[] = etichette.map((e) => ({ value: e.id, label: e.nome }));
  const etichettaById = new Map(etichette.map((e) => [e.id, e]));

  const etichettaAccount = (id: string | null) =>
    id === null
      ? 'Tutte le caselle'
      : (accounts.find((a) => a.id === id)?.indirizzo ?? 'casella rimossa');

  const resetBuilder = () => {
    setEditId(null);
    setNome('');
    setAccountId(TUTTE_LE_CASELLE);
    setMatchTipo('all');
    setCondizioni([{ campo: 'mittente', operatore: 'contiene', valore: '' }]);
    setAzioniSel(new Set());
    setCartellaSposta('');
    setEtichettaApplicata('');
    setStopSuMatch(false);
    setPriorita(0);
    setDryRun(null);
    setBackfill(null);
    setErrore(null);
  };

  const apriNuova = () => {
    resetBuilder();
    setEditId('');
  };

  const apriModifica = (r: EmailRegola) => {
    resetBuilder();
    setEditId(r.id);
    setNome(r.nome);
    setAccountId(r.account_id ?? TUTTE_LE_CASELLE);
    setMatchTipo(r.match_tipo);
    setCondizioni(
      r.condizioni.map((c) => ({
        campo: c.campo,
        operatore: c.operatore,
        valore: String(c.valore),
      }))
    );
    setAzioniSel(new Set(r.azioni.map((a) => a.tipo)));
    setCartellaSposta(r.azioni.find((a) => a.tipo === 'sposta')?.cartella_id ?? '');
    setEtichettaApplicata(
      r.azioni.find((a) => a.tipo === 'applica_etichetta')?.etichetta_id ?? ''
    );
    setStopSuMatch(r.stop_su_match);
    setPriorita(r.priorita);
  };

  const toggleAzione = (tipo: string) => {
    setAzioniSel((prev) => {
      const next = new Set(prev);
      if (next.has(tipo)) {
        next.delete(tipo);
      } else {
        // Max una azione di spostamento: selezionarne una deseleziona le altre
        const def = REGOLA_AZIONI.find((a) => a.value === tipo);
        if (def?.spostamento) {
          for (const a of REGOLA_AZIONI) {
            if (a.spostamento) next.delete(a.value);
          }
        }
        next.add(tipo);
      }
      return next;
    });
  };

  const validaForm = (): string | null => {
    if (!nome.trim()) return 'Il nome della regola e obbligatorio';
    if (condizioni.length === 0) return 'Serve almeno una condizione';
    for (const c of condizioni) {
      if (!c.valore.trim()) return 'Ogni condizione deve avere un valore';
    }
    if (azioniSel.size === 0) return 'Serve almeno una azione';
    if (azioniSel.has('sposta')) {
      if (!accountId) return "L'azione Sposta richiede una casella specifica";
      if (!cartellaSposta) return "Scegli la cartella di destinazione per l'azione Sposta";
    }
    if (azioniSel.has('applica_etichetta') && !etichettaApplicata) {
      return "Scegli l'etichetta da applicare per l'azione Applica etichetta";
    }
    return null;
  };

  const costruisciPayload = () => {
    const cond: RegolaCondizione[] = condizioni.map((c) =>
      c.campo === 'ha_allegati'
        ? { campo: c.campo, operatore: 'uguale', valore: c.valore === 'true' }
        : { campo: c.campo, operatore: c.operatore, valore: c.valore }
    );
    // Azioni non di spostamento prima, spostamento per ultima
    const ordinate = REGOLA_AZIONI.filter((a) => azioniSel.has(a.value)).sort(
      (a, b) => Number(a.spostamento) - Number(b.spostamento)
    );
    const azioni = ordinate.map((a) => {
      if (a.value === 'sposta') return { tipo: a.value, cartella_id: cartellaSposta };
      if (a.value === 'applica_etichetta') {
        return { tipo: a.value, etichetta_id: etichettaApplicata };
      }
      return { tipo: a.value };
    });
    return {
      nome: nome.trim(),
      account_id: accountId || null,
      priorita,
      match_tipo: matchTipo,
      condizioni: cond,
      azioni,
      stop_su_match: stopSuMatch,
    };
  };

  const salva = async () => {
    const err = validaForm();
    if (err) {
      setErrore(err);
      return;
    }
    setSalvataggio(true);
    setErrore(null);
    try {
      const res = editId
        ? await emailFetch(`/regole/${editId}`, {
            method: 'PATCH',
            body: JSON.stringify(costruisciPayload()),
          })
        : await emailFetch('/regole', {
            method: 'POST',
            body: JSON.stringify(costruisciPayload()),
          });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        setErrore(
          typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`
        );
        return;
      }
      resetBuilder();
      await carica();
    } catch {
      setErrore('Errore di rete');
    } finally {
      setSalvataggio(false);
    }
  };

  // Guard anti doppio-click: un solo PATCH in volo per regola (il vecchio
  // badge-toggle senza guard permetteva ping-pong attiva/disattivata).
  const [togglePending, setTogglePending] = useState<string | null>(null);
  const toggleAttiva = async (r: EmailRegola) => {
    if (togglePending) return;
    setTogglePending(r.id);
    try {
      await emailFetch(`/regole/${r.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ attiva: !r.attiva }),
      });
      await carica();
    } finally {
      setTogglePending(null);
    }
  };

  const elimina = async (r: EmailRegola) => {
    if (!window.confirm(`Eliminare la regola "${r.nome}"?`)) return;
    await emailFetch(`/regole/${r.id}`, { method: 'DELETE' });
    if (editId === r.id) resetBuilder();
    await carica();
  };

  const provaDryRun = async () => {
    if (!editId) return;
    setDryRun(null);
    setBackfill(null);
    const res = await emailFetch(`/regole/${editId}/dry-run?limit=200`, { method: 'POST' });
    if (res.ok) {
      setDryRun(await res.json());
    } else {
      const body = await res.json().catch(() => null);
      setErrore(typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`);
    }
  };

  const applicaBackfill = async () => {
    if (!editId) return;
    const dettaglio = dryRun ? ` (dry-run: ${dryRun.match.length} match)` : '';
    if (
      !window.confirm(
        `Applicare la regola ai messaggi gia' presenti in INBOX${dettaglio}? Le azioni verranno eseguite davvero.`
      )
    ) {
      return;
    }
    const res = await emailFetch(`/regole/${editId}/backfill?limit=200`, { method: 'POST' });
    if (res.ok) {
      setBackfill(await res.json());
    } else {
      const body = await res.json().catch(() => null);
      setErrore(typeof body?.detail === 'string' ? body.detail : `Errore ${res.status}`);
    }
  };

  const regoleVisibili = filtroAccount
    ? regole.filter((r) => r.account_id === filtroAccount || r.account_id === null)
    : regole;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} aria-label="Indietro">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="text-lg font-semibold">Regole email</h1>
      </div>

      <Card className="p-4">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-medium">Regole di smistamento</h2>
          <div className="flex items-center gap-2">
            <div className="w-52">
              <SearchSelect
                id="filtro-account"
                options={opzioniAccountFiltro}
                value={filtroAccount}
                onChange={(v) =>
                  setSearchParams(v ? { account: v } : {}, { replace: true })
                }
                placeholder="Filtra per casella"
              />
            </div>
            <Button size="sm" onClick={apriNuova}>
              <Plus className="mr-1 h-4 w-4" />
              Nuova regola
            </Button>
          </div>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">
          Le regole si applicano ai nuovi messaggi in arrivo in INBOX, in ordine di priorita
          (piu' bassa = prima).
        </p>

        {loading && <p className="mt-3 text-sm text-muted-foreground">Caricamento...</p>}
        {!loading && regoleVisibili.length === 0 && (
          <p className="mt-3 text-sm text-muted-foreground">Nessuna regola.</p>
        )}

        <ul className="mt-3 divide-y">
          {regoleVisibili.map((r) => (
            <li key={r.id} className="flex items-center gap-3 py-3">
              <div className="flex-1">
                <p className="text-sm font-medium">{r.nome}</p>
                <p className="text-xs text-muted-foreground">
                  {etichettaAccount(r.account_id)} - priorita {r.priorita} -{' '}
                  {r.condizioni.length} condizioni
                  {r.stop_su_match ? ' - stop al match' : ''}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  {r.azioni.map((a, i) => {
                    const def = REGOLA_AZIONI.find((x) => x.value === a.tipo);
                    const et =
                      a.tipo === 'applica_etichetta' && a.etichetta_id
                        ? etichettaById.get(a.etichetta_id)
                        : undefined;
                    return (
                      <span key={i} className="text-xs text-muted-foreground">
                        {def?.label ?? a.tipo}
                        {et && (
                          <>
                            {' '}
                            <span
                              className="inline-block h-2 w-2 rounded-full align-middle"
                              style={{ backgroundColor: et.colore }}
                              aria-hidden="true"
                            />{' '}
                            {et.nome}
                          </>
                        )}
                      </span>
                    );
                  })}
                </div>
              </div>
              {/* switch esplicito on/off (stato + azione inequivocabili) */}
              <label className="flex cursor-pointer select-none items-center gap-2">
                <span
                  className={`text-xs ${r.attiva ? 'text-foreground' : 'text-muted-foreground'}`}
                >
                  {r.attiva ? 'Attiva' : 'Disattivata'}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={r.attiva}
                  aria-label={`Regola ${r.nome} ${r.attiva ? 'attiva' : 'disattivata'}`}
                  disabled={togglePending === r.id}
                  onClick={() => toggleAttiva(r)}
                  className={`relative h-5 w-9 rounded-full transition-colors disabled:opacity-50 ${
                    r.attiva ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-all ${
                      r.attiva ? 'left-[18px]' : 'left-0.5'
                    }`}
                  />
                </button>
              </label>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => apriModifica(r)}
                aria-label={`Modifica ${r.nome}`}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => elimina(r)}
                aria-label={`Elimina ${r.nome}`}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </li>
          ))}
        </ul>
      </Card>

      {editId !== null && (
        <Card className="space-y-3 p-4">
          <div className="flex items-center justify-between">
            <h2 className="font-medium">{editId ? `Modifica regola: ${nome}` : 'Nuova regola'}</h2>
            <Button variant="ghost" size="sm" onClick={resetBuilder} aria-label="Chiudi builder">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label htmlFor="regola-nome">Nome</Label>
              <Input
                id="regola-nome"
                value={nome}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNome(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="regola-account">Casella</Label>
              <SearchSelect
                id="regola-account"
                options={opzioniAccountFiltro}
                value={accountId}
                onChange={(v) => {
                  setAccountId(v);
                  setCartellaSposta('');
                }}
              />
            </div>
            <div>
              <Label htmlFor="regola-priorita">Priorita</Label>
              <Input
                id="regola-priorita"
                type="number"
                value={priorita}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPriorita(Number(e.target.value))
                }
              />
            </div>
          </div>

          <div>
            <Label>Condizioni</Label>
            <div className="mt-1 flex items-center gap-2 text-xs">
              <span>Devono valere</span>
              <div className="flex rounded-md border">
                <button
                  type="button"
                  className={`rounded-l-md px-2 py-1 ${matchTipo === 'all' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                  onClick={() => setMatchTipo('all')}
                >
                  tutte
                </button>
                <button
                  type="button"
                  className={`rounded-r-md px-2 py-1 ${matchTipo === 'any' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground'}`}
                  onClick={() => setMatchTipo('any')}
                >
                  almeno una
                </button>
              </div>
            </div>
            <div className="mt-2 space-y-2">
              {condizioni.map((c, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
                  <SearchSelect
                    id={`cond-campo-${i}`}
                    options={REGOLA_CAMPI}
                    value={c.campo}
                    onChange={(v) =>
                      setCondizioni((prev) =>
                        prev.map((x, j) =>
                          j === i
                            ? v === 'ha_allegati'
                              ? { campo: v, operatore: 'uguale', valore: 'true' }
                              : { ...x, campo: v }
                            : x
                        )
                      )
                    }
                  />
                  {c.campo === 'ha_allegati' ? (
                    <>
                      <Input value="uguale a" disabled />
                      <SearchSelect
                        id={`cond-valore-${i}`}
                        options={OPZIONI_SI_NO}
                        value={c.valore}
                        onChange={(v) =>
                          setCondizioni((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, valore: v } : x))
                          )
                        }
                      />
                    </>
                  ) : (
                    <>
                      <SearchSelect
                        id={`cond-op-${i}`}
                        options={REGOLA_OPERATORI}
                        value={c.operatore}
                        onChange={(v) =>
                          setCondizioni((prev) =>
                            prev.map((x, j) => (j === i ? { ...x, operatore: v } : x))
                          )
                        }
                      />
                      <Input
                        id={`cond-valore-${i}`}
                        value={c.valore}
                        placeholder="valore"
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          setCondizioni((prev) =>
                            prev.map((x, j) =>
                              j === i ? { ...x, valore: e.target.value } : x
                            )
                          )
                        }
                      />
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setCondizioni((prev) => prev.filter((_, j) => j !== i))
                    }
                    disabled={condizioni.length === 1}
                    aria-label="Rimuovi condizione"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <button
              type="button"
              className="mt-2 text-xs text-muted-foreground underline"
              onClick={() =>
                setCondizioni((prev) => [
                  ...prev,
                  { campo: 'mittente', operatore: 'contiene', valore: '' },
                ])
              }
            >
              + aggiungi condizione
            </button>
          </div>

          <div>
            <Label>Azioni</Label>
            <div className="mt-2 flex flex-wrap gap-2">
              {REGOLA_AZIONI.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  className={`rounded-md border px-3 py-1.5 text-xs ${
                    azioniSel.has(a.value)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground'
                  }`}
                  onClick={() => toggleAzione(a.value)}
                >
                  {a.label}
                </button>
              ))}
            </div>
            {azioniSel.has('sposta') && (
              <div className="mt-2 w-64">
                <Label htmlFor="regola-cartella">Cartella di destinazione</Label>
                <SearchSelect
                  id="regola-cartella"
                  options={opzioniCartelle}
                  value={cartellaSposta}
                  onChange={setCartellaSposta}
                  placeholder={accountId ? 'Cerca cartella...' : 'Scegli prima una casella'}
                />
              </div>
            )}
            {azioniSel.has('applica_etichetta') && (
              <div className="mt-2 w-64">
                <Label htmlFor="regola-etichetta">Etichetta da applicare</Label>
                <SearchSelect
                  id="regola-etichetta"
                  options={opzioniEtichette}
                  value={etichettaApplicata}
                  onChange={setEtichettaApplicata}
                  placeholder="Cerca etichetta..."
                />
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={stopSuMatch}
              onChange={(e) => setStopSuMatch(e.target.checked)}
            />
            Ferma le regole successive quando questa regola scatta
          </label>

          {errore && <p className="text-sm text-destructive">{errore}</p>}

          {dryRun && (
            <div className="rounded-md border p-3">
              <p className="text-sm font-medium">
                Dry-run: {dryRun.match.length} match su {dryRun.totale_esaminati} messaggi
                esaminati (nessuna azione eseguita)
              </p>
              <ul className="mt-2 max-h-40 space-y-1 overflow-auto text-xs text-muted-foreground">
                {dryRun.match.map((m) => (
                  <li key={m.id}>
                    {m.da_nome || m.da_email || '-'} - {m.oggetto || '(senza oggetto)'}
                    {m.data_ricezione
                      ? ` - ${new Date(m.data_ricezione).toLocaleString('it-IT')}`
                      : ''}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {backfill && (
            <div className="rounded-md border p-3 text-sm">
              Applicata ai messaggi esistenti: {backfill.match} match su {backfill.esaminati}{' '}
              esaminati, azioni riuscite {backfill.azioni_ok}, fallite {backfill.azioni_err}.
            </div>
          )}

          <div className="flex justify-end gap-2">
            {editId && (
              <>
                <Button variant="outline" onClick={provaDryRun}>
                  <Play className="mr-1 h-4 w-4" />
                  Prova (dry-run)
                </Button>
                <Button variant="outline" onClick={applicaBackfill}>
                  Applica agli esistenti
                </Button>
              </>
            )}
            <Button variant="outline" onClick={resetBuilder} disabled={salvataggio}>
              Annulla
            </Button>
            <Button onClick={salva} disabled={salvataggio}>
              {salvataggio ? 'Salvataggio...' : editId ? 'Salva modifiche' : 'Crea regola'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
